/**
 * CID Codes List Page
 * List and manage CID-10 codes (Classificação Internacional de Doenças)
 *
 * @version 2025.1.0
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Assessment as StatsIcon,
  FileDownload as FileDownloadIcon,
  FileUpload as FileUploadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { PageShell, DataTable, type DataTableColumn } from '../../components';
import { cidCodeService } from '../../services/cidCodeService';
import { CidCode, CidCodeFilters, CidCodeStatistics, CidCategory, getCategoryDescription } from '../../types/cidCode';
import {
  CidCodesFiltersComponent,
  CidCodesImportDialog,
} from './components';
import { pode } from '../../authorization/pode';
import { useAcao, useControle } from '../../authorization/useVeredito';
import { ActionButton, RouteActionButton } from '../../components/common';

// Shape emitted live by CidCodesFiltersComponent's onFilter (mirrors its internal filter state).
interface CidCodesFiltersData {
  search: string;
  code: string;
  category: string;
  isActive: string;
}

const columns: DataTableColumn<CidCode>[] = [
  {
    id: 'code',
    label: 'Código',
    primary: true,
    render: (cidCode) => (
      <Chip
        label={cidCode.code}
        size="small"
        color="primary"
        variant="outlined"
        sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}
      />
    ),
  },
  {
    id: 'description',
    label: 'Descrição',
    render: (cidCode) => (
      <Typography variant="body2" sx={{ maxWidth: 400 }}>
        {cidCode.description}
      </Typography>
    ),
  },
  {
    id: 'category',
    label: 'Categoria',
    render: (cidCode) => (
      <Tooltip title={getCategoryDescription(cidCode.category)}>
        <Chip label={cidCode.category} size="small" variant="outlined" />
      </Tooltip>
    ),
  },
  {
    id: 'subcategory',
    label: 'Subcategoria',
    render: (cidCode) =>
      cidCode.subcategory ? (
        <Chip label={cidCode.subcategory} size="small" variant="outlined" color="secondary" />
      ) : (
        <Typography variant="body2" color="text.secondary">
          -
        </Typography>
      ),
  },
  {
    id: 'isActive',
    label: 'Status',
    render: (cidCode) => (
      <Tooltip title={cidCode.isActive ? 'Ativo' : 'Inativo'}>
        {cidCode.isActive ? <ActiveIcon color="success" /> : <InactiveIcon color="error" />}
      </Tooltip>
    ),
  },
];

export const CidCodesListPage: React.FC = () => {
  const navigate = useNavigate();
  const editar = useControle('/cid-codes/:id/edit', pode('cid-code.update'), 'Editar');
  const excluir = useAcao('cid-code.delete', 'Excluir');
  const [cidCodes, setCidCodes] = useState<CidCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState<CidCodeFilters>({});
  const [statistics, setStatistics] = useState<CidCodeStatistics | null>(null);
  const [categories, setCategories] = useState<CidCategory[]>([]);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const loadCidCodes = async (
    pageNumber: number = page,
    limit: number = rowsPerPage,
    filterOverride: CidCodeFilters = filters,
  ) => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: pageNumber + 1,
        limit,
        ...filterOverride,
      };

      const response = await cidCodeService.getCidCodes(params);

      setCidCodes(response.cidCodes);
      setTotal(response.total);
    } catch (err: unknown) {
      console.error('Error loading CID codes:', err);
      setError((err as Error).message ?? 'Erro ao carregar códigos CID-10');
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const stats = await cidCodeService.getStatistics();
      setStatistics(stats);
    } catch (err) {
      console.error('Error loading statistics:', err);
    }
  };

  const loadCategories = async () => {
    try {
      const cats = await cidCodeService.getCategories();
      setCategories(cats);
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  };

  useEffect(() => {
    loadCidCodes();
    loadStatistics();
    loadCategories();
  }, [page, rowsPerPage]);

  const handleFilter = (data: CidCodesFiltersData) => {
    const apiFilters: CidCodeFilters = {
      search: data.search || undefined,
      code: data.code || undefined,
      category: data.category || undefined,
      isActive: data.isActive === '' ? undefined : data.isActive === 'true',
    };

    setFilters(apiFilters);
    setPage(0);
    loadCidCodes(0, rowsPerPage, apiFilters);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este código CID-10? O registro será desativado.')) {
      return;
    }

    try {
      await cidCodeService.deleteCidCode(id);
      loadCidCodes();
      loadStatistics();
    } catch (err: unknown) {
      setError((err as Error).message ?? 'Erro ao excluir código CID-10');
    }
  };

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleEdit = (cidCode: CidCode) => {
    navigate(`/cid-codes/${cidCode.id}/edit`);
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const blob = await cidCodeService.exportCidCodes();
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().slice(0, 10);
      link.download = `cid-codes-export-${timestamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: unknown) {
      setError((err as Error).message ?? 'Erro ao exportar códigos CID-10');
    } finally {
      setExportLoading(false);
    }
  };

  const handleImportComplete = () => {
    loadCidCodes();
    loadStatistics();
  };

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Códigos CID-10' },
      ]}
      title="Códigos CID-10"
      subtitle="Classificação Internacional de Doenças - 10ª Revisão"
      actions={
        <>
          <Button
            variant="outlined"
            startIcon={exportLoading ? <CircularProgress size={16} /> : <FileDownloadIcon />}
            onClick={handleExport}
            disabled={exportLoading}
          >
            Exportar
          </Button>
          <ActionButton
            acao="cid-code.import"
            rotulo="Importar"
            variant="outlined"
            startIcon={<FileUploadIcon />}
            onClick={() => setImportDialogOpen(true)}
          >
            Importar
          </ActionButton>
          <RouteActionButton
            para="/cid-codes/new"
            exige={pode('cid-code.create')}
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
          >
            Novo Código
          </RouteActionButton>
        </>
      }
    >
      {/* Statistics Cards */}
      {statistics && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Total de Códigos
              </Typography>
              <Typography variant="h4">{statistics.totalCodes}</Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Códigos Ativos
              </Typography>
              <Typography variant="h4" color="success.main">
                {statistics.activeCodes}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Códigos Inativos
              </Typography>
              <Typography variant="h4" color="text.secondary">
                {statistics.inactiveCodes}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Categorias
              </Typography>
              <Typography variant="h4" color="primary.main">
                {statistics.categoriesCount}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {error && !loading && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ mb: 3 }}>
        <CidCodesFiltersComponent
          categories={categories}
          onFilter={handleFilter}
          loading={loading}
          total={total}
        />
      </Box>

      {/* Table */}
      {statistics && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Chip
            icon={<StatsIcon />}
            label={`${statistics.activeCodes} códigos ativos`}
            color="success"
            variant="outlined"
          />
        </Box>
      )}
      <DataTable
          columns={columns}
          rows={cidCodes}
          getRowId={(cidCode) => cidCode.id}
          loading={loading}
          emptyState={{
            title: 'Nenhum código CID-10 encontrado',
            description: 'A CID-10 é uma tabela de referência do sistema, usada nos atestados e nas licenças médicas — use a busca acima para localizar um código.',
          }}
          actions={[
            {
              icon: <EditIcon fontSize="small" />,
              onClick: handleEdit,
              ...editar,
            },
            {
              icon: <DeleteIcon fontSize="small" />,
              color: 'error',
              onClick: (cidCode) => handleDelete(cidCode.id),
              ...excluir,
            },
          ]}
          pagination={{
            page,
            rowsPerPage,
            totalCount: total,
            onPageChange: handleChangePage,
            onRowsPerPageChange: handleChangeRowsPerPage,
            rowsPerPageOptions: [10, 25, 50, 100],
          }}
      />

      {/* Import Dialog */}
      <CidCodesImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImportComplete={handleImportComplete}
      />
    </PageShell>
  );
};

export default CidCodesListPage;
